import DOMPurify from 'dompurify';
import { convertFromRaw, EditorState } from 'draft-js';
import moment from 'moment';
import Papa from 'papaparse';
import { Tooltip } from 'antd';
import React from 'react';
import {
  NOTE_VENDORS,
  USER_TYPE_TO_DETAIL_KEY,
} from '../constants/NoteConstants';

/**
 * Checks if a string is a url
 * Needs improvements over time
 * @param {*} string
 */
export function isTextURL(string) {
  const tester =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

  return string && string.match(tester);

  // try {
  //   if (text && new URL(text)) {
  //     return true;
  //   }
  // } catch (error) {
  //   return false;
  // }
  // return false;
}

/**
 * Opens a link in a new tab
 * @param {*} url
 */
export function openInNewTab(url) {
  const win = window.open(url, '_blank', 'noopener');
  win.focus();
}

/**
 * @param {List} resultList
 * @param {string} keyAttribute
 */
export default function getIndexedMap(resultList, keyAttribute) {
  const resultMap = {};

  resultList.forEach((column) => {
    resultMap[column[keyAttribute]] = column;
  });

  return resultMap;
}

export function shouldShowCalculationViewPopover(row) {
  return (
    row &&
    (row.horizontal ||
      row.vertical ||
      row.horizontalCustom ||
      (row.parentRelationships && row.parentRelationships.length > 0))
  );
}
export function capitalizeFirstLetter(string) {
  return string ? string[0].toUpperCase() + string.slice(1) : string;
}

export function extractById(list, ObjId) {
  return list.find(({ id }) => {
    return id === +ObjId;
  });
}

export function extractByKey(list, ObjValue, key) {
  return list.find(({ [key]: _key }) => {
    return _key === ObjValue;
  });
}

export function stringToBoolean(_str) {
  return (
    (typeof _str === 'boolean' && _str) ||
    (typeof _str === 'string' && _str.toLowerCase() === 'true')
  );
}

/**
 * Access the values inside an object dynamically using list of keys.
 *
 * @param {Object} _object
 * @param {Array<String>} _keys
 */
export function extractValue(_object, _keys) {
  return _object && _keys.length > 0
    ? extractValue(_object[_keys[0]], _keys.slice(1))
    : _object;
}

export const formatDateTime = (date) => {
  return date && moment(date).local().format('DD MMM YYYY hh:mm A');
};

export const isSameDate = (date1, date2) => {
  return moment(date1).isSame(date2, 'second');
};

export const formatDate = (date, format = 'DD MMM YYYY') => {
  return date && moment(date).local().format(format);
};

export const formatTime = (date, format = 'hh:mm A') => {
  return date && moment(date).local().format(format);
};

export const monthDiff = (date1, date2) => {
  return moment(date2).diff(moment(date1), 'months');
};

export const daysDiff = (date1, date2) => {
  return moment(date2).diff(moment(date1), 'days');
};

export const extractFileExtension = (fileName) => {
  return fileName.toLowerCase().split('.').reverse()[0];
};

// Maximum file upload size = 25MB
export const isValidFileSize = (fileSize) => {
  return fileSize / 1024 / 1024 < 25;
};

// Maximum image upload size = 2MB
export const isValidImageSize = (imageSize) => {
  return imageSize / 1024 / 1024 < 2;
};

export const sortAttributes = (attributes, childParam) => {
  const system = [];
  const special = [];
  const custom = [];

  attributes.forEach((attribute) => {
    const checkAttrib = childParam ? attribute[childParam] : attribute;
    if (checkAttrib.startsWith('system:')) {
      system.push(attribute);
    } else if (checkAttrib.startsWith('special:')) {
      special.push(attribute);
    } else {
      custom.push(attribute);
    }
  });
  return [...system, ...special, ...custom];
};

export const getOrdinalNum = (number) => {
  let selector;

  if (number <= 0) {
    selector = 4;
  } else if ((number > 3 && number < 21) || number % 10 > 3) {
    selector = 0;
  } else {
    selector = number % 10;
  }

  return number + ['th', 'st', 'nd', 'rd', ''][selector];
};

export function customSleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function replaceValue(array, index, newValue) {
  const _array = [...array];
  _array[index] = newValue;
  return _array;
}

export function validateAndSetEditorState(editorRawTxt, setEditorState) {
  if (editorRawTxt) {
    let _editorState;
    try {
      _editorState = EditorState.createWithContent(
        convertFromRaw(JSON.parse(editorRawTxt))
      );
    } catch (e) {
      console.error('Invalid description');
      _editorState = EditorState.createEmpty();
    }
    setEditorState(_editorState);
  } else {
    setEditorState(EditorState.createEmpty());
  }
}

export function validateAndSetRichTextState(editorRawTxt, setEditorState) {
  if (editorRawTxt) {
    let _editorState;
    try {
      _editorState = JSON.parse(editorRawTxt);
    } catch (e) {
      console.error('Invalid description');
      _editorState = null;
    }
    setEditorState(_editorState);
  } else {
    setEditorState(null);
  }
}

export const isNumber = (value) => !Number.isNaN(Number(value));
export function includesIn(sourceArray, targetArray) {
  return sourceArray.some((_field) => targetArray.includes(_field));
}

export const isBlank = (value) => value === undefined || value === null;

export function booleanToNumber(_bool) {
  return Number(_bool);
}

export const createMarkup = (html) => {
  return {
    __html: DOMPurify.sanitize(html, null),
  };
};

export const jsonParse = (rawData) => {
  try {
    return JSON.parse(rawData);
  } catch (e) {
    console.error('Invalid description format');
    return null;
  }
};

export const jsonParseRichText = (rawData) => {
  try {
    return JSON.parse(rawData);
  } catch (e) {
    console.error('Invalid description format');
    return '';
  }
};

export const formatNumber = (value) => {
  const intValue = value.match(/\d+/g).join('');
  const formattedIntSubValue = new Intl.NumberFormat().format(intValue);
  const nonIntValue = value.replace(/[\d\s.]+/g, '');
  return `${formattedIntSubValue} ${nonIntValue}`;
};

/**
 * Only works for primitive arrays!!
 *
 * @param {*} array1
 * @param {*} array2
 * @returns
 */
export function isArrayEqual(array1 = [], array2 = []) {
  if (array1 && array2) {
    return (
      array1.length === array2.length &&
      array1.every((value, index) => value === array2[index])
    );
  }
  return false;
}

/**
 * Only works for primitive arrays!!
 *
 * @param {*} array1
 * @param {*} array2
 * @returns
 */
export const equals = (array1, array2) => {
  const array2Sorted = array2.slice().sort();
  return (
    array1.length === array2.length &&
    array1
      .slice()
      .sort()
      .every((value, index) => value === array2Sorted[index])
  );
};

/**
 * Will return 2 arrays for True and False results of the condition provide.
 *
 * @param {*} array
 * @param {*} condition
 * @returns
 */
export const partition = (array, condition) => {
  return array.reduce(
    (partitioned, element) => {
      if (condition(element)) {
        partitioned[0].push(element);
      } else {
        partitioned[1].push(element);
      }
      return partitioned;
    },
    [[], []]
  );
};

export const unique = (array) => [...new Set(array)];

export const isUnique = (array) => unique(array).length === array.length;

/**
 * Checks intersection between array 1 and array 2.
 * @param {*} array1
 * @param {*} array2
 * @returns
 */
export const isIntersects = (array1, array2) => {
  if (!array1 || !array2) {
    return false;
  }

  return array1.some((value) => array2.includes(value));
};

export const duplicated = (array) => {
  return unique(array.filter((value, index) => array.indexOf(value) !== index));
};

export const isObjectHasAllValues = (obj) => {
  return !Object.values(obj).some((value) => !value);
};
// Truncate String with Ellipsis
export const truncatEllipsis = (str = '', cl = 50) => {
  return str.length > cl ? `${str.substring(0, cl)}...` : str;
};

export const truncateComplexStringWithEllipsis = (string = '', length = 30) => {
  if (string.length <= length) {
    return string;
  }

  let truncated = string.slice(0, length);
  const lastSpaceIndex = Math.max(
    truncated.lastIndexOf(' '),
    truncated.lastIndexOf('_')
  );
  if (lastSpaceIndex !== -1) {
    truncated = truncated.slice(0, lastSpaceIndex);
  }

  return `${truncated}...`;
};

// check obj is empty
export const areAllAttributesNull = (obj) => {
  return Object.values(obj).every((val) => !val);
};

export const copyTextToClipboard = async (text) => {
  if ('clipboard' in navigator) {
    // eslint-disable-next-line no-return-await
    return await navigator.clipboard.writeText(text);
  }
  return document.execCommand('copy', true, text);
};

export const isEmail = (email) => {
  return /[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/.test(email);
};

export const toSentenceCase = (word) => {
  return word?.[0].toUpperCase() + word?.slice(1).toLowerCase();
};

export const getOrdinalSuffix = (number) => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return 'th';
  }
  if (lastDigit === 1) {
    return 'st';
  }
  if (lastDigit === 2) {
    return 'nd';
  }
  if (lastDigit === 3) {
    return 'rd';
  }
  return 'th';
};

export const assignUserDetails = (criteria, userType, sourceData) => {
  const detailKey = USER_TYPE_TO_DETAIL_KEY[userType];
  if (detailKey) {
    return { [`${criteria}${detailKey}`]: sourceData };
  }
  return {};
};

export const isVendorSource = (source) => {
  return Object.values(NOTE_VENDORS).some((vendor) => vendor.value === source);
};

/**
 * Returns the latest of the two provided ISO date strings.
 *
 * @param {string} date1
 * @param {string} date2
 * @returns {string}
 */
export const getLatestDate = (date1, date2) => {
  if (!date1) return date2;
  if (!date2) return date1;
  return new Date(Math.max(Date.parse(date1), Date.parse(date2))).toISOString();
};

export const splitAtIndex = (value, index) => {
  return [value.substring(0, index), value.substring(index + 1)];
};

export const max = (arr, minimumValue = -Infinity, getValue) => {
  return arr.reduce((a, b) => {
    if (getValue) {
      return Math.max(a, getValue(b));
    }
    return Math.max(a, b);
  }, minimumValue);
};

export const isValueInObject = (obj, value) => {
  return Object.values(obj).includes(value);
};

export const checkIfCsvHasUniqueHeaders = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (results) => {
        const headers = results.meta.fields;
        const uniqueHeaders = [...new Set(headers)];
        if (headers.length !== uniqueHeaders.length) {
          return reject(new Error('Columns are not unique!'));
        }

        return resolve();
      },
    });
  });
};

export const deviation = (number1, number2, maxDeviation) =>
  (number1 - number2 + maxDeviation) % maxDeviation;

export const convertRemToPx = (remValue) => {
  return (
    parseFloat(remValue) *
    parseFloat(getComputedStyle(document.documentElement).fontSize)
  );
};

export const fullName = (user) => {
  return `${user?.firstName?.trim() || ''} ${
    user?.lastName?.trim() || ''
  }`.trim();
};

export const hideFirstCharacters = (str, length = 10, replaceChar = '*') => {
  if (str.length <= length) {
    return str; // If string length is 10 or less, return original string
  }
  return replaceChar.repeat(length) + str.substring(length);
};

export const renderInputLabel = (label, tooltip) => {
  return tooltip ? (
    <span className="form-label-suffix icon">
      {label}
      <Tooltip title={tooltip}>
        <i className="icon core-icon core-info suffix-icon" />
      </Tooltip>
    </span>
  ) : (
    label
  );
};

export const isMeaningfulDescription = (des) => {
  if (!des) return false;
  const markup = createMarkup(jsonParseRichText(des));
  const text = markup?.__html;
  return text.length > 0;
};

export const prettyPrintList = (stringList) => {
  if (!stringList.length) return '';
  if (stringList.length === 1) return stringList[0];

  const allButLast = stringList.slice(0, -1);
  const last = stringList[stringList.length - 1];

  return `${allButLast.join(', ')} and ${last}`;
};
